import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Stack,
  Typography,
  Box,
  Alert,
  AlertTitle,
} from '@mui/material';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import SouthEastIcon from '@mui/icons-material/SouthEast';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useWaterLevelWithObservations } from 'features/waterLevel/hooks/useWaterLevelWithObservations';
import { InlineStatus } from 'components/InlineStatus';
import { MapEntityDetailsLayout } from 'features/mapping/components/MapEntityDetailsLayout';
import { StatItem, StatTable } from 'components/StatTable';
import { subDays } from 'date-fns';
import { getTimeAgo } from 'shared/dates';
import { Capacitor } from '@capacitor/core';
import { useShareOrCopy } from 'hooks/useShareOrCopy';
import { formatNumber, getCurrentLocationAsShareUrl } from 'shared/utils';
import { WeatherAlertList } from 'features/weatherAlerts/components/WeatherAlertList';
import { useTabs } from 'hooks/useTabs/useTabs';
import { useWaterLevelsChart } from 'features/waterLevel/hooks/useWaterLevelsChart';
import { EchartsWrapper } from 'libs/echarts/EchartsWrapper';
import { MapLayers } from 'features/mapping/mapping.constants';
import { FloodImpacts } from 'features/waterLevel/components/FloodImpacts';
import { Loader } from 'components/Loader';
import { ChartLoadingGuard } from 'components/ChartLoadingGuard';
import { useRequireMapLayerForRouterEntity } from 'hooks/useRequireMapLayerForRouterEntity';
import { FloodLevelChip } from 'features/waterLevel/components/FloodLevelChip';
import {
  formatWaterLevelValue,
  getFloodCategoryFromLevel,
  numericWaterLevelForDisplay,
  shouldDisplayWaterLevelForecast,
  waterLevelsEqualForDisplay,
} from 'features/waterLevel/waterLevel.utils';
import {
  EMPTY_DATA_VALUE,
  WEATHER_OBSERVATIONS_POLL_INTERVAL,
} from '../../constants';
import { WeatherStatValue } from '../WeatherStationDetails/WeatherStatValue';

const TAB_PANEL_ID_PREFIX = 'water-level-tab-panel';
// Full-bleed chart compensation values
const CHART_FULL_BLEED_MARGIN_LEFT = -0.5; // -4px to offset container gap
const CHART_FULL_BLEED_WIDTH_EXTENSION = 2.25; // +18px to extend to edges

type IconComponent = typeof ArrowOutwardIcon | typeof SouthEastIcon;

const getForecastTrendIcon = (
  currentLevel: number | undefined,
  forecastPeakLevel: number | undefined,
): IconComponent | null => {
  if (
    currentLevel === undefined ||
    forecastPeakLevel === undefined ||
    waterLevelsEqualForDisplay(forecastPeakLevel, currentLevel)
  ) {
    return null;
  }
  return numericWaterLevelForDisplay(forecastPeakLevel) >
    numericWaterLevelForDisplay(currentLevel)
    ? ArrowOutwardIcon
    : SouthEastIcon;
};

export const WaterLevelDetails = (): JSX.Element => {
  const { gaugeId: gaugeIdString } = useParams<{ gaugeId: string }>();
  const { t } = useTranslation();
  const { canShareOrCopy, shareOrCopyUrl } = useShareOrCopy();
  const observationsHistoryStart = subDays(new Date(), 7);
  const gaugeId = parseInt(gaugeIdString, 10);

  useRequireMapLayerForRouterEntity(MapLayers.WATER_LEVEL);

  const {
    waterLevel,
    observations,
    forecasts,
    isLoadingStation,
    isLoadingObservations,
    isLoadingForecasts,
    error,
  } = useWaterLevelWithObservations({
    id: gaugeId,
    start: observationsHistoryStart,
    end: new Date(),
    pollInterval: WEATHER_OBSERVATIONS_POLL_INTERVAL,
  });

  const waterLevelsChartOptions = useWaterLevelsChart({
    observations: observations || [],
    forecasts: forecasts || [],
    waterLevel,
  });

  const isOffline = Boolean(waterLevel?.isOffline);
  const hasObservations = Boolean(observations?.length);
  const canDisplayChart = !isOffline || hasObservations;
  const isChartDataLoading = isLoadingObservations || isLoadingForecasts;

  const latestLevel = observations?.length ? observations[0].level : undefined;

  // Calculate forecast peak level
  const forecastPeakLevel =
    forecasts.length > 0
      ? Math.max(...forecasts.map((f) => f.level))
      : undefined;

  const tabData = [
    {
      tabId: 'conditions',
      label: t('waterLevel.tabs.conditions'),
      enabled: true,
      content: (
        <Stack sx={{ py: 2, pb: 0, overflowX: 'hidden' }} spacing={2}>
          <Stack spacing={0}>
            {isOffline && (
              <Alert severity="error" sx={{ m: 2, mt: 0 }}>
                <AlertTitle sx={hasObservations ? { mb: 0 } : undefined}>
                  {t('waterLevel.offline')}
                </AlertTitle>
                {!hasObservations && t('waterLevel.offlineNoObservations')}
              </Alert>
            )}
            <Box
              sx={{
                px: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="body1">
                <b>{t('waterLevel.chart.title')}</b>{' '}
                <Typography
                  component="span"
                  color="text.secondary"
                  variant="body1"
                >
                  {t('waterLevel.chart.units', {
                    units: waterLevel?.units.level,
                  })}
                </Typography>
              </Typography>
              {waterLevel?.noaaLid && (
                <Typography variant="body2">
                  <Typography
                    component="span"
                    color="text.secondary"
                    variant="body2"
                  >
                    {t('waterLevel.chart.source')}
                  </Typography>{' '}
                  <Link
                    to={{
                      pathname: `https://water.noaa.gov/gauges/${waterLevel.noaaLid}`,
                    }}
                    target="_blank"
                    rel="nofollow"
                    style={{
                      color: 'inherit',
                      display: 'inline-flex',
                      alignItems: 'center',
                    }}
                  >
                    <b>{t('waterLevel.chart.noaa')}</b>{' '}
                    <OpenInNewIcon fontSize="inherit" />
                  </Link>
                </Typography>
              )}
            </Box>
            {canDisplayChart && waterLevelsChartOptions && (
              <ChartLoadingGuard isLoading={isChartDataLoading}>
                <Box
                  sx={(theme) => ({
                    // Full bleed: extend chart to panel edges
                    // Compensates for container padding/gaps to achieve full-width chart
                    marginLeft: theme.spacing(CHART_FULL_BLEED_MARGIN_LEFT),
                    width: `calc(100% + ${theme.spacing(CHART_FULL_BLEED_WIDTH_EXTENSION)})`,
                  })}
                >
                  <EchartsWrapper option={waterLevelsChartOptions} />
                </Box>
              </ChartLoadingGuard>
            )}
          </Stack>
          {waterLevel?.floodImpacts && waterLevel.floodImpacts.length > 0 && (
            <FloodImpacts
              floodImpacts={waterLevel.floodImpacts}
              floodCategories={waterLevel.floodCategories}
              currentLevel={latestLevel}
              levelUnits={waterLevel.units.level}
              forecastPeakLevel={forecastPeakLevel}
            />
          )}
        </Stack>
      ),
    },
    {
      tabId: 'notifications',
      label: t('waterLevel.tabs.notifications'),
      enabled: Capacitor.isNativePlatform(),
      content: (
        <WeatherAlertList
          station={{ id: gaugeId, weatherStationType: 'waterLevel' }}
          fromRoute={`/gauge/${gaugeId}?tab=notifications`}
          showStationName={false}
        />
      ),
    },
  ];

  const { tabsProps, tabProps, tabPanels } = useTabs({
    idPrefix: TAB_PANEL_ID_PREFIX,
    ariaLabel: t('waterLevel.tabs.ariaLabel'),
    tabData,
  });

  if (isLoadingStation || error || !waterLevel) {
    return <InlineStatus status={isLoadingStation ? 'loading' : 'error'} />;
  }

  const { flow: latestFlow = undefined, timestamp: lastUpdated = undefined } =
    observations.length ? observations[0] : {};

  const handleShare = (): void => {
    shareOrCopyUrl({
      title: t('home.waterLevelTitle', { name: waterLevel.name }),
      url: getCurrentLocationAsShareUrl(lastUpdated),
    });
  };

  const latestCategory = latestLevel
    ? getFloodCategoryFromLevel(latestLevel, waterLevel.floodCategories)
    : null;

  const showForecastPeakStat = shouldDisplayWaterLevelForecast({
    floodCategories: waterLevel.floodCategories,
    currentLevel: latestLevel,
    forecastPeakLevel,
  });

  const TrendIcon = showForecastPeakStat
    ? getForecastTrendIcon(latestLevel, forecastPeakLevel)
    : null;

  const stats: StatItem[] = [
    {
      label: t('waterLevel.height'),
      value: (
        <Stack
          component="span"
          direction="row"
          spacing={0.25}
          useFlexGap
          flexWrap="wrap"
          alignItems="center"
          justifyContent="center"
        >
          <WeatherStatValue
            value={latestLevel}
            i18nKey="common.valueWithUnits"
            transValues={{
              value: formatWaterLevelValue(latestLevel ?? 0),
              units: waterLevel.units.level,
            }}
            valuePlaceholder={0}
            unitPlaceholder={waterLevel.units.level}
          />
          {TrendIcon && <TrendIcon sx={{ fontSize: 16, mx: 0.5 }} />}
          {showForecastPeakStat && (
            <WeatherStatValue
              value={forecastPeakLevel}
              i18nKey="common.valueWithUnits"
              transValues={{
                value: formatWaterLevelValue(forecastPeakLevel ?? 0),
                units: waterLevel.units.level,
              }}
              valuePlaceholder={0}
              unitPlaceholder={waterLevel.units.level}
            />
          )}
        </Stack>
      ),
    },
    {
      label: t('waterLevel.floodLevel'),
      value:
        latestLevel !== undefined ? (
          <FloodLevelChip
            category={latestCategory}
            hasFloodCategories={waterLevel.floodCategories.length > 0}
          />
        ) : (
          EMPTY_DATA_VALUE
        ),
    },
    {
      label: t('waterLevel.flow'),
      value: (
        <WeatherStatValue
          value={latestFlow}
          i18nKey="common.valueWithUnits"
          transValues={{
            value: formatNumber(latestFlow ?? 0),
            units: waterLevel.units.flow,
          }}
          valuePlaceholder={0}
          unitPlaceholder={waterLevel.units.flow}
        />
      ),
      tooltipText:
        latestFlow && waterLevel.units.flow === 'kcfs'
          ? t('waterLevel.kcfs')
          : undefined,
    },
  ];

  return (
    <>
      <Helmet>
        <title>{t('home.waterLevelTitle', { name: waterLevel.name })}</title>
      </Helmet>

      <MapEntityDetailsLayout.Root>
        <MapEntityDetailsLayout.Header title={waterLevel.name} />

        <MapEntityDetailsLayout.Body>
          <Box sx={{ position: 'relative' }}>
            <StatTable stats={stats} />
            {isLoadingObservations && (
              <Loader disableBackground color="inherit" size={28} />
            )}
          </Box>

          {lastUpdated && (
            <MapEntityDetailsLayout.Meta>
              <Typography variant="body2">
                {t('common.updated')}&nbsp;
                <b>{getTimeAgo(lastUpdated.toISOString())}</b>
              </Typography>
            </MapEntityDetailsLayout.Meta>
          )}

          {canShareOrCopy && (
            <MapEntityDetailsLayout.Actions>
              <Button
                fullWidth
                color="neutral"
                variant="outlined"
                onClick={handleShare}
                size="large"
              >
                {t('common.share')}
              </Button>
            </MapEntityDetailsLayout.Actions>
          )}
        </MapEntityDetailsLayout.Body>

        <MapEntityDetailsLayout.Tabs
          tabsProps={tabsProps}
          tabProps={tabProps}
          tabPanels={tabPanels}
        />
      </MapEntityDetailsLayout.Root>
    </>
  );
};
